import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@patient-watch/ui/form";
import { Label } from "@patient-watch/ui/label";
import { cn } from "@patient-watch/ui";
import { Star as StarIcon } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { RichLabel } from "./components/RichLabel";

import type { InputT } from "@patient-watch/types";

interface Props {
    input: InputT;
    name: string;
    label: string;
}

interface StarOption {
    label: string;
    value: number;
}

const Star = ({ input, name, label }: Props) => {
    const { control } = useFormContext();
    const options = (input.scale_options ?? []) as unknown as StarOption[];

    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => {
                const selectedValue = typeof field.value === "number" ? field.value : Number(field.value) || 0;

                return (
                    <FormItem>
                        <FormLabel>
                            <Label>
                                <RichLabel>{label}</RichLabel>
                            </Label>
                            {!input.is_optional && <span className="ml-0.5 text-red-500">*</span>}
                        </FormLabel>
                        <FormControl>
                            <ul className="flex flex-row flex-wrap items-end gap-2">
                                {options.map((option) => {
                                    const isFilled = selectedValue >= option.value;

                                    return (
                                        <li key={option.value} className="flex flex-col items-center gap-1">
                                            <button
                                                type="button"
                                                aria-label={`${option.value} star${option.value === 1 ? "" : "s"}`}
                                                onClick={() => field.onChange(option.value)}
                                                className={cn(
                                                    "flex justify-center items-center p-1 rounded-md transition-colors",
                                                    "hover:bg-amber-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2"
                                                )}
                                            >
                                                <StarIcon
                                                    size={32}
                                                    className={cn(
                                                        "transition-colors",
                                                        isFilled ? "fill-amber-400 text-amber-500" : "text-slate-300"
                                                    )}
                                                />
                                            </button>
                                            {option.label && (
                                                <span className="text-muted-foreground text-xs text-center leading-tight">
                                                    {option.label}
                                                </span>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
};

export default Star;
